import { useQuery } from "@tanstack/react-query";
import { lazy, useRef, type ComponentType } from "react";
import { Navigate, Route, Routes } from "react-router";
import { api, ApiError } from "@/api";
import { AppShell } from "@/components/AppShell";
import { DayPage } from "@/pages/DayPage";
import { EnrollPage } from "@/pages/EnrollPage";
import { LoginPage } from "@/pages/LoginPage";
import { NowPage } from "@/pages/NowPage";
import { OpenSharePage } from "@/pages/OpenSharePage";

// Loaded when first opened, so the Day and Now screens start fast (a TV's browser can be slow).
const page = <K extends string>(load: () => Promise<Record<K, ComponentType<any>>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));
const EntryPage = page(() => import("@/pages/EntryPage"), "EntryPage");
const RunsPage = page(() => import("@/pages/RunsPage"), "RunsPage");
const SearchPage = page(() => import("@/pages/SearchPage"), "SearchPage");
const WeekPage = page(() => import("@/pages/WeekPage"), "WeekPage");
const SharesPage = page(() => import("@/pages/SharesPage"), "SharesPage");
const DevicesPage = page(() => import("@/pages/DevicesPage"), "DevicesPage");
const StatsLayout = page(() => import("@/pages/stats/StatsLayout"), "StatsLayout");
const OverviewPage = page(() => import("@/pages/stats/OverviewPage"), "OverviewPage");
const CalendarPage = page(() => import("@/pages/stats/CalendarPage"), "CalendarPage");
const TrendsPage = page(() => import("@/pages/stats/TrendsPage"), "TrendsPage");

export function App() {
  // Polled so a revoked link or an expired session shows up, and so open tabs learn about a deploy.
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 60_000, refetchInterval: 5 * 60_000 });
  const firstBuild = useRef<string | null>(null);
  if (me.data && firstBuild.current === null) firstBuild.current = me.data.buildId;
  const updateReady = !!me.data && me.data.buildId !== firstBuild.current;

  if (me.isPending) return <div className="grid min-h-screen place-items-center text-sm text-muted">Loading…</div>;
  if (me.isError) {
    const message = me.error instanceof ApiError ? me.error.message : "Can't reach the server.";
    return <div className="grid min-h-screen place-items-center text-sm text-danger">{message}</div>;
  }

  const m = me.data;
  return (
    <Routes>
      <Route path="/s" element={<OpenSharePage />} />
      <Route path="/enroll" element={<EnrollPage me={m} />} />
      {m.role === "none" ? (
        <Route path="*" element={<LoginPage me={m} />} />
      ) : (
        <>
          <Route path="/now" element={<NowPage me={m} updateReady={updateReady} />} />
          <Route element={<AppShell me={m} updateReady={updateReady} />}>
            <Route path="/day" element={<DayPage me={m} />} />
            <Route path="/day/:date" element={<DayPage me={m} />} />
            <Route path="/week" element={<WeekPage me={m} />} />
            <Route path="/week/:date" element={<WeekPage me={m} />} />
            <Route path="/entry/:id" element={<EntryPage me={m} />} />
            {(m.share?.journal ?? true) && <Route path="/runs" element={<RunsPage me={m} />} />}
            {m.role === "owner" && !m.preview && <Route path="/search" element={<SearchPage me={m} />} />}
            <Route path="/stats" element={<StatsLayout />}>
              <Route index element={<OverviewPage me={m} />} />
              <Route path="calendar" element={<CalendarPage me={m} />} />
              <Route path="trends" element={<TrendsPage me={m} />} />
            </Route>
            {m.role === "owner" && !m.preview && <Route path="/shares" element={<SharesPage me={m} />} />}
            {m.role === "owner" && !m.preview && <Route path="/devices" element={<DevicesPage me={m} />} />}
            <Route path="*" element={<Navigate to="/day" replace />} />
          </Route>
        </>
      )}
    </Routes>
  );
}
